export const careerEn = {
  career: {
    profile: {
      page: {
        title: 'My Profile',
        subtitle: 'This is what recruiters and your future CVs are built from.',
        save: 'Save',
        error_prefix: 'Failed to load your profile.',
        completeness: 'Profile completeness: {score}%',
      },
      fields: {
        headline: {
          label: 'Headline',
          placeholder: 'e.g. Senior Backend Engineer',
        },
        summary: {
          label: 'Summary',
          placeholder: 'A short overview of your professional background.',
        },
        location: {
          label: 'Location',
          placeholder: 'e.g. Warsaw, Poland',
        },
        visibility: {
          label: 'Visibility',
          options: {
            PRIVATE: 'Private — only you can see this',
            FRIENDS: 'Friends — coming soon, currently same as Private',
            PUBLIC: 'Public — visible to anyone with the link',
          },
        },
        contact: {
          title: 'Contact information',
          subtitle: 'Only shown to you — not included on your public profile page.',
          email: 'Email',
          phone: 'Phone',
          linkedin: 'LinkedIn',
          website: 'Website',
        },
        slug: {
          label: 'Public profile URL',
        },
      },
    },
    publicProfile: {
      page: {
        title: 'Profile',
      },
      not_found: 'This profile does not exist or is not public.',
      error: 'Failed to load this profile.',
    },
    experiences: {
      page: {
        title: 'Experience',
        subtitle: 'Your work history — this feeds every CV you generate.',
        add: 'Add experience',
        empty: 'No experience entries yet. Add your first one.',
        error: 'Failed to load your experience entries.',
      },
      form: {
        createTitle: 'Add experience',
        editTitle: 'Edit experience',
        subtitle: 'Describe a role you held, its dates, and the technologies you used.',
      },
      fields: {
        companyName: 'Company',
        position: 'Position',
        employmentType: 'Employment type',
        employmentTypePlaceholder: 'e.g. full_time, contract, freelance',
        startDate: 'Start date',
        endDate: 'End date',
        isCurrent: 'I currently work here',
        description: 'Description',
        responsibilities: 'Responsibilities',
        responsibilitiesPlaceholder: 'Add a responsibility and press Enter',
        technologies: 'Technologies',
        technologiesPlaceholder: 'Search or add a technology',
      },
      deleteConfirm: {
        title: 'Delete this experience?',
        description: 'This will permanently remove this experience entry from your profile.',
      },
    },
    skills: {
      page: {
        title: 'Skills',
        subtitle: 'Rate your proficiency in the technologies you know.',
        add: 'Add skill',
        empty: 'No skills yet. Add your first one.',
        error: 'Failed to load your skills.',
      },
      form: {
        createTitle: 'Add skill',
        editTitle: 'Edit skill',
        subtitle: 'Pick a technology and rate your proficiency in it.',
      },
      fields: {
        technology: 'Technology',
        level: 'Proficiency (1-5)',
        yearsOfExperience: 'Years of experience',
        yearsOfExperienceShort: '{years} yrs',
        startedUsingYear: 'Started using (year)',
        isPrimary: 'Primary skill',
      },
      deleteConfirm: {
        title: 'Delete this skill?',
        description: 'This will permanently remove this skill from your profile.',
      },
    },
    projects: {
      page: {
        title: 'Projects',
        subtitle: 'Portfolio projects — document them once, link them to any role.',
        add: 'Add project',
        empty: 'No projects yet. Add your first one.',
        error: 'Failed to load your projects.',
      },
      form: {
        createTitle: 'Add project',
        editTitle: 'Edit project',
        subtitle: 'Describe the project, then optionally link the experience(s) and technologies behind it.',
      },
      fields: {
        name: 'Project name',
        role: 'Your role',
        description: 'Description',
        startDate: 'Start date',
        endDate: 'End date',
        isOngoing: 'This project is ongoing',
        isAnonymized: 'Anonymize the company/client name',
        anonymizedCompany: 'Anonymized company label',
        anonymizedCompanyPlaceholder: 'e.g. Fortune 500 Bank',
        status: 'Status',
        category: 'Category',
        categoryPlaceholder: 'Select a category',
        achievements: 'Achievements',
        achievementsPlaceholder: 'Add an achievement and press Enter',
        challenges: 'Challenges',
        challengesPlaceholder: 'Add a challenge and press Enter',
        clients: 'Clients',
        clientsPlaceholder: 'Add a client and press Enter',
        team: 'Team members',
        teamPlaceholder: 'Add a colleague name and press Enter',
        subProjects: 'Sub-projects',
        subProjectNamePlaceholder: 'Name, e.g. Client site',
        subProjectUrlPlaceholder: 'URL (optional)',
        addSubProject: 'Add sub-project',
        teamSize: 'Team size',
        durationMonths: 'Duration (months)',
        usersCount: 'Users count',
        budgetRange: 'Budget range',
        budgetRangePlaceholder: 'e.g. $50k-100k',
        links: 'Links',
        demo: 'Demo URL',
        github: 'GitHub URL',
        docs: 'Docs URL',
        visibility: 'Visibility',
        technologies: 'Technologies',
        linkedExperiences: 'Linked experiences',
        noExperiences: 'Add a work experience first to link it to this project.',
      },
      statusOptions: {
        ACTIVE: 'Active',
        STAGING: 'Staging',
        ARCHIVED: 'Archived',
      },
      categoryOptions: {
        DEMO: 'Demo',
        INTERNAL: 'Internal',
        PRODUCTION: 'Production',
      },
      anonymized: 'Anonymized client',
      deleteConfirm: {
        title: 'Delete this project?',
        description: 'This will permanently remove this project from your profile.',
      },
    },
    education: {
      page: {
        title: 'Education',
        subtitle: 'Degrees and courses that shaped your background.',
        add: 'Add education',
        empty: 'No education entries yet. Add your first one.',
        error: 'Failed to load your education entries.',
      },
      form: {
        createTitle: 'Add education',
        editTitle: 'Edit education',
        subtitle: 'Add a degree, course, or program you completed.',
      },
      fields: {
        institution: 'Institution',
        degree: 'Degree',
        fieldOfStudy: 'Field of study',
        startDate: 'Start date',
        endDate: 'End date',
        present: 'Present',
        grade: 'Grade',
        gradePlaceholder: 'e.g. 3.8 GPA, First Class Honours',
        description: 'Description',
      },
      deleteConfirm: {
        title: 'Delete this education entry?',
        description: 'This will permanently remove this entry from your profile.',
      },
    },
    certifications: {
      page: {
        title: 'Certifications',
        subtitle: 'Professional certifications and credentials.',
        add: 'Add certification',
        empty: 'No certifications yet. Add your first one.',
        error: 'Failed to load your certifications.',
      },
      form: {
        createTitle: 'Add certification',
        editTitle: 'Edit certification',
        subtitle: 'Add a certification and its credential details.',
      },
      fields: {
        name: 'Certification name',
        issuingOrganization: 'Issuing organization',
        credentialId: 'Credential ID',
        credentialUrl: 'Credential URL',
        issueDate: 'Issue date',
        expiryDate: 'Expiry date',
      },
      expired: 'Expired',
      deleteConfirm: {
        title: 'Delete this certification?',
        description: 'This will permanently remove this certification from your profile.',
      },
    },
    achievements: {
      page: {
        title: 'Achievements',
        subtitle: 'Awards, publications, and speaking engagements.',
        add: 'Add achievement',
        empty: 'No achievements yet. Add your first one.',
        error: 'Failed to load your achievements.',
      },
      form: {
        createTitle: 'Add achievement',
        editTitle: 'Edit achievement',
        subtitle: 'Add an award, publication, or speaking engagement.',
      },
      fields: {
        title: 'Title',
        description: 'Description',
        date: 'Date',
        category: 'Category',
        categoryPlaceholder: 'Select a category',
        url: 'URL',
      },
      categoryOptions: {
        AWARD: 'Award',
        PUBLICATION: 'Publication',
        SPEAKING: 'Speaking',
        OTHER: 'Other',
      },
      deleteConfirm: {
        title: 'Delete this achievement?',
        description: 'This will permanently remove this achievement from your profile.',
      },
    },
    languages: {
      page: {
        title: 'Languages',
        subtitle: 'Spoken and written languages with CEFR levels.',
        add: 'Add language',
        empty: 'No languages yet. Add your first one.',
        error: 'Failed to load your languages.',
      },
      form: {
        createTitle: 'Add language',
        editTitle: 'Edit language',
        subtitle: 'Add a language and your proficiency level.',
      },
      fields: {
        name: 'Language',
        namePlaceholder: 'e.g. English, Polish',
        level: 'Level',
        levelPlaceholder: 'Select a level',
        description: 'Notes',
      },
      levelOptions: {
        NATIVE: 'Native',
        C2: 'C2 — Proficiency',
        C1: 'C1 — Advanced',
        B2: 'B2 — Upper intermediate',
        B1: 'B1 — Intermediate',
        A2: 'A2 — Elementary',
        A1: 'A1 — Beginner',
      },
      deleteConfirm: {
        title: 'Delete this language?',
        description: 'This will permanently remove this language from your profile.',
      },
    },
    cvVersions: {
      page: {
        title: 'CV Versions',
        subtitle: 'Curated selections of your profile, tailored per application.',
        add: 'Add CV version',
        empty: 'No CV versions yet. Add your first one.',
        error: 'Failed to load your CV versions.',
        preview: 'Preview',
        generate: 'Generate PDF',
        download: 'Download PDF',
        generateSuccess: 'PDF generated — ready to download.',
        generateSuccessWatermarked: 'PDF generated with a watermark (Free plan) — ready to download.',
        downloadNotGenerated: 'No PDF has been generated for this CV version yet.',
      },
      form: {
        createTitle: 'Add CV version',
        editTitle: 'Edit CV version',
        subtitle: 'Pick a name and template, then choose which profile sections to include.',
      },
      fields: {
        name: 'Name',
        template: 'Template',
        customAccentColor: 'Custom accent color',
        fontFamily: 'Font',
        density: 'Spacing',
        customSummary: 'Custom summary (optional)',
        customSummaryPlaceholder: 'Override your profile summary for this CV',
        includeSummary: 'Include summary',
        includePhoto: 'Include photo',
        isDefault: 'Default',
        experiences: 'Experiences',
        noExperiences: 'No experiences on your profile yet.',
        projects: 'Projects',
        noProjects: 'No projects on your profile yet.',
        skills: 'Skills',
        noSkills: 'No skills on your profile yet.',
        education: 'Education',
        noEducation: 'No education entries on your profile yet.',
        certifications: 'Certifications',
        noCertifications: 'No certifications on your profile yet.',
        achievements: 'Achievements',
        noAchievements: 'No achievements on your profile yet.',
        languages: 'Languages',
        noLanguages: 'No languages on your profile yet.',
      },
      templateOptions: {
        default: 'Default',
        modern: 'Modern',
        classic: 'Classic',
        minimal: 'Minimal',
        sidebar: 'Sidebar',
        timeline: 'Timeline',
        compact: 'Compact (ATS-friendly)',
      },
      fontOptions: {
        sans: 'Sans',
        'modern-sans': 'Modern Sans',
        serif: 'Serif',
        'minimal-sans': 'Minimal Sans',
        mono: 'Monospace',
      },
      densityOptions: {
        compact: 'Compact',
        standard: 'Standard',
        spacious: 'Spacious',
      },
      deleteConfirm: {
        title: 'Delete this CV version?',
        description: 'This will permanently remove this CV version from your profile.',
      },
    },
    overview: {
      title: 'Your career at a glance',
      subtitle: 'Everything your CVs are built from, in one place.',
      completeness: 'Profile strength',
      completenessHint: 'Fill in your profile and sections to reach 100%.',
      error: 'Failed to load your career overview.',
      sections: {
        experiences: 'Experiences',
        projects: 'Projects',
        skills: 'Skills',
        education: 'Education',
        certifications: 'Certifications',
        achievements: 'Achievements',
        languages: 'Languages',
        cvVersions: 'CV versions',
      },
      suggestions: {
        title: 'Suggested next steps',
        addHeadline: 'Add a headline to your profile',
        addExperience: 'Add your first work experience',
        addSummary: 'Write a professional summary',
        addSkills: 'Add at least 3 skills',
        addProject: 'Document your first project',
        addEducation: 'Add your education',
        addLanguage: 'Add the languages you speak',
        createCv: 'Create your first CV version',
        addPhoto: 'Upload a profile photo',
        makePublic: 'Make your profile public to share it',
      },
      quickActions: {
        editProfile: 'Edit profile',
        addExperience: 'Add experience',
        newCv: 'New CV version',
        viewPublic: 'View public profile',
      },
    },
    ai: {
      optimizeDescription: {
        button: 'Optimize with AI',
        success: 'Description optimized',
      },
      suggestResponsibilities: {
        button: 'Suggest with AI',
        success: '{count} responsibilities added',
        needsRole: 'Enter a position first',
      },
      suggestSkills: {
        title: 'Suggest skills for a role',
        button: 'Suggest with AI',
        rolePlaceholder: 'e.g. Senior Backend Engineer',
        empty: 'Enter a role and click Suggest to get skill ideas — click a suggestion to add it.',
      },
      gapAnalysis: {
        title: 'AI gap analysis',
        subtitle: 'See how your profile matches a target role, with AI-generated strengths, gaps, and recommendations.',
        targetRolePlaceholder: 'e.g. Senior Backend Engineer',
        button: 'Analyze',
        matchScore: 'Match score',
        strengths: 'Strengths',
        gaps: 'Gaps',
        recommendations: 'Recommendations',
        empty: 'Enter a target role and click Analyze.',
      },
      error: {
        accessDenied: 'AI features require a Pro or Expert subscription, or your own OpenRouter API key in AI settings.',
      },
    },
  },
}
